import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Image, Save, Search, Check, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function ImageEditorPage({ embedded = false }) {
  const { projectId } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const limit = 20;

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: 'title_original',
        sort_order: 'asc'
      });
      if (search) params.append('search', search);

      const res = await api.get(`/projects/${projectId}/products?${params}`);
      setProducts(res.data.products);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      toast.error('Nepodařilo se načíst produkty');
    } finally {
      setLoading(false);
    }
  }, [projectId, page, search]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleSelectImage = async (product, imageUrl) => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/products/${product.id}/image`, {
        image_link: imageUrl
      });
      toast.success('Obrázek uložen');
      setSelectedProduct(null);
      fetchProducts();
    } catch (err) {
      toast.error('Nepodařilo se uložit obrázek');
    } finally {
      setSaving(false);
    }
  };

  const getAllImages = (product) => {
    const images = [];
    if (product.image_link) {
      images.push(product.image_link);
    }
    if (product.additional_image_links && product.additional_image_links.length > 0) {
      images.push(...product.additional_image_links);
    }
    return images;
  };

  const getCurrentImage = (product) => {
    return product.selected_image_link || product.image_link;
  };

  if (loading && products.length === 0) {
    return (
      <div className="p-6 lg:p-8">
        <Skeleton className="h-8 w-48 mb-8" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Skeleton key={i} className="aspect-square" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'p-6 lg:p-8'}>
      {!embedded && (
        <div className="flex items-center gap-4 mb-6">
          <Button asChild variant="ghost" size="icon" data-testid="back-btn">
            <Link to={`/projects/${projectId}`}>
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-heading font-bold text-slate-900">Editor obrázků</h1>
            <p className="text-sm text-slate-500">Vyberte hlavní obrázek pro každý produkt</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Hledat produkty..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-input"
          />
        </div>
      </div>

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Image className="w-12 h-12 text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">Žádné produkty</h3>
          <p className="text-slate-500">Importujte produkty z XML feedu</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {products.map((product) => {
            const allImages = getAllImages(product);
            const hasMultipleImages = allImages.length > 1;
            const currentImage = getCurrentImage(product);
            
            return (
              <Card 
                key={product.id}
                className={`border border-slate-200 overflow-hidden cursor-pointer hover:border-amber-300 transition-colors ${
                  hasMultipleImages ? 'ring-2 ring-amber-200' : ''
                }`}
                onClick={() => hasMultipleImages && setSelectedProduct(product)}
                data-testid={`product-card-${product.id}`}
              >
                <div className="aspect-square bg-slate-100 relative">
                  {currentImage ? (
                    <img 
                      src={currentImage} 
                      alt={product.title_original}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Image className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  
                  {hasMultipleImages && (
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-amber-500 text-slate-900 font-semibold">
                        {allImages.length} obr.
                      </Badge>
                    </div>
                  )}
                  
                  {product.selected_image_link && (
                    <div className="absolute top-2 left-2">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}
                </div>
                <CardContent className="p-3">
                  <p className="text-sm font-medium text-slate-900 line-clamp-2">
                    {product.title_modified || product.title_original}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{product.price || '-'}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-slate-500">
            Zobrazeno {(page - 1) * limit + 1} - {Math.min(page * limit, total)} z {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm px-3">
              {page} / {pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page === pages}
              data-testid="next-page-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Image Selection Dialog */}
      <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Image className="w-5 h-5 text-amber-500" />
              Vyberte obrázek
            </DialogTitle>
            <DialogDescription>
              {selectedProduct?.title_modified || selectedProduct?.title_original}
            </DialogDescription>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
              {getAllImages(selectedProduct).map((imageUrl, index) => {
                const isSelected = getCurrentImage(selectedProduct) === imageUrl;
                
                return (
                  <div
                    key={index}
                    className={`relative aspect-square bg-slate-100 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                      isSelected 
                        ? 'border-amber-500 ring-2 ring-amber-200' 
                        : 'border-slate-200 hover:border-amber-300'
                    }`}
                    onClick={() => handleSelectImage(selectedProduct, imageUrl)}
                    data-testid={`image-option-${index}`}
                  >
                    <img 
                      src={imageUrl} 
                      alt={`Obrázek ${index + 1}`}
                      className="w-full h-full object-contain"
                    />
                    
                    {isSelected && (
                      <div className="absolute top-2 right-2">
                        <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                          <Check className="w-5 h-5 text-slate-900" />
                        </div>
                      </div>
                    )}
                    
                    {index === 0 && (
                      <div className="absolute bottom-2 left-2">
                        <Badge variant="outline" className="bg-white/90 text-xs">
                          Hlavní
                        </Badge>
                      </div>
                    )}
                    
                    {saving && (
                      <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
